import { useCarStoreContext } from '../../components/fleet-calculator/store/car-store-provider';
import { CalcModel } from '../../components/fleet-calculator/types';

export default function Settings() {
  const allCarModels = useCarStoreContext((state) => state.allCarModels);
  const updateState = useCarStoreContext((state) => state.updateState);
  const activeTrimCodes = useCarStoreContext((state) => state.activeTrimCodes);
  const selectableTrimCodes = useCarStoreContext(
    (state) => state.selectableTrimCodes,
  );
  const isCompactLayout = useCarStoreContext((state) => state.isCompactLayout);

  const hasSelectedTrim = selectableTrimCodes.length > 0;

  const handleModelCheckboxChange = (model: CalcModel, isChecked: boolean) => {
    const modelTrimCodes = model.calculator_cars.map((trim) => trim.code);
    let newSelectableTrimCodes;
    if (isChecked) {
      newSelectableTrimCodes = [
        ...new Set([...selectableTrimCodes, ...modelTrimCodes]),
      ];
    } else {
      newSelectableTrimCodes = selectableTrimCodes.filter(
        (code) => !modelTrimCodes.includes(code),
      );
    }
    updateState({ selectableTrimCodes: newSelectableTrimCodes });
  };

  const handleTrimCheckboxChange = (trimCode: string, isChecked: boolean) => {
    let newSelectableTrimCodes;
    if (isChecked) {
      newSelectableTrimCodes = [...new Set([...selectableTrimCodes, trimCode])];
    } else {
      newSelectableTrimCodes = selectableTrimCodes.filter(
        (code) => code !== trimCode,
      );
    }
    updateState({ selectableTrimCodes: newSelectableTrimCodes });
  };

  const handleSelectAll = () => {
    if (!allCarModels) return;
    const allTrimCodes = allCarModels.flatMap((model) =>
      model.calculator_cars.map((trim) => trim.code),
    );
    updateState({ selectableTrimCodes: allTrimCodes });
  };

  const handleDeselectAll = () => {
    updateState({ selectableTrimCodes: [] });
  };

  const handleSetTrim = (code: string, pos: 'left' | 'right') => {
    const newTrimCodes = [...(activeTrimCodes || [])];
    const index = pos === 'left' ? 0 : 1;
    newTrimCodes[index] = code;
    updateState({ activeTrimCodes: newTrimCodes });
  };

  const getModelSelectionState = (
    model: CalcModel,
  ): 'all' | 'some' | 'none' => {
    const modelTrimCodes = model.calculator_cars.map((t) => t.code);
    const selectedCount = modelTrimCodes.filter((code) =>
      selectableTrimCodes.includes(code),
    ).length;

    if (selectedCount === 0) return 'none';
    if (selectedCount === modelTrimCodes.length) return 'all';
    return 'some';
  };

  return (
    <div className="max-w-3xl mx-auto p-4 font-sans bg-white rounded-lg">
      <div className="space-y-2">
        <div className="grid grid-cols-2 min-h-11 border border-black">
          <button
            data-active={isCompactLayout === false}
            onClick={() => updateState({ isCompactLayout: false })}
            className="data-[active='true']:bg-black data-[active='true']:text-white transition-colors"
          >
            Full view
          </button>
          <button
            data-active={isCompactLayout === true}
            onClick={() => updateState({ isCompactLayout: true })}
            className="data-[active='true']:bg-black data-[active='true']:text-white transition-colors"
          >
            Compact view
          </button>
        </div>
        <div className="flex justify-between text-gray-800 border-b pb-2">
          <h1 className="text-2xl font-bold ">Configure Snippet</h1>
          {!hasSelectedTrim ? (
            <button
              onClick={handleSelectAll}
              className="bg-black text-white cursor-pointer px-6"
            >
              Select all
            </button>
          ) : (
            <button
              onClick={handleDeselectAll}
              className="bg-black text-white cursor-pointer px-6 "
            >
              Deselect All
            </button>
          )}
        </div>
        {allCarModels.map((model) => {
          const selectionState = getModelSelectionState(model);
          const selectionCount = model.calculator_cars.filter((c) =>
            selectableTrimCodes.includes(c.code),
          ).length;

          return (
            <div
              key={model.id}
              className="model-accordion border rounded-md overflow-hidden"
            >
              <details>
                <summary className="flex items-center p-3 cursor-pointer gap-2 hover:bg-gray-100 transition-colors duration-150 list-none">
                  <input
                    type="checkbox"
                    title="check"
                    className="mr-3 size-5 rounded"
                    checked={selectionState === 'all'}
                    ref={(el) =>
                      el && (el.indeterminate = selectionState === 'some')
                    }
                    onChange={(e) =>
                      handleModelCheckboxChange(model, e.target.checked)
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="flex-grow text-lg font-semibold text-gray-700">
                    {model.name}
                  </span>
                  <span className="text-sm text-gray-500 mr-2">
                    ({selectionCount} / {model.calculator_cars.length})
                  </span>
                </summary>
                <div className="trims-list bg-gray-50 border-t border-gray-200 py-2">
                  {model.calculator_cars.map((trim) => (
                    <div
                      key={trim.id}
                      className="trim-item flex items-center px-4 py-2 hover:bg-gray-100 gap-2"
                    >
                      <div className="w-8"></div>
                      <input
                        type="checkbox"
                        id={`trim-${trim.id}`}
                        className="mr-3 h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                        checked={selectableTrimCodes.includes(trim.code)}
                        onChange={(e) =>
                          handleTrimCheckboxChange(trim.code, e.target.checked)
                        }
                      />
                      <label
                        htmlFor={`trim-${trim.id}`}
                        className="flex-grow text-gray-700 cursor-pointer"
                      >
                        {trim.name}
                      </label>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          );
        })}
      </div>
      {hasSelectedTrim && (
        <div className="grid gap-4 p-2 rounded-md border mt-4">
          <div className="grid ">
            <label className="font-bold" htmlFor="first-trim">
              Left trim
            </label>
            <select
              className="p-3 cursor-pointer hover:bg-gray-100 transition-colors duration-150 border rounded-md"
              id="first-trim"
              name="first-trim"
              value={activeTrimCodes?.[0] || ''}
              onChange={(e) => {
                const selectedCode = e.target.value;
                if (!selectedCode) return;
                const selectedCar = allCarModels
                  ?.flatMap((model) => model.calculator_cars)
                  .find((car) => car.code === selectedCode);
                if (selectedCar) {
                  handleSetTrim(selectedCar.code, 'left');
                }
              }}
            >
              <option value="">Select left trim</option>
              {allCarModels
                ?.flatMap((model) =>
                  model.calculator_cars.filter((car) =>
                    selectableTrimCodes.includes(car.code),
                  ),
                )
                .filter((car) => car.code !== activeTrimCodes?.[1])
                .map((car) => (
                  <option key={car.id} value={car.code}>
                    {car.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="grid ">
            <label className="font-bold" htmlFor="second-trim">
              Right trim
            </label>
            <select
              className="p-3 cursor-pointer hover:bg-gray-100 transition-colors duration-150 border rounded-md"
              id="second-trim"
              name="second-trim"
              value={activeTrimCodes?.[1] || ''}
              onChange={(e) => {
                const selectedCode = e.target.value;
                if (!selectedCode) return;
                const selectedCar = allCarModels
                  ?.flatMap((model) => model.calculator_cars)
                  .find((car) => car.code === selectedCode);
                if (selectedCar) {
                  handleSetTrim(selectedCar.code, 'right');
                }
              }}
            >
              <option value="">Select right trim</option>
              {allCarModels
                ?.flatMap((model) =>
                  model.calculator_cars.filter((car) =>
                    selectableTrimCodes.includes(car.code),
                  ),
                )
                .filter((car) => car.code !== activeTrimCodes?.[0])
                .map((car) => (
                  <option key={car.id} value={car.code}>
                    {car.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
